import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

// Session management functions
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

// User credentials (stored securely in code)
const VALID_USERS = [
  { email: 'kalyan@natureiq.com', password: 'Natureiq@2026', name: 'Kalyan' },
  { email: 'shirish@natureiq.com', password: 'Natureiq@2026', name: 'Shirish' },
  { email: 'amol@natureiq.com', password: 'Natureiq@2026', name: 'Amol' },
  { email: 'admin@natureiq.com', password: 'admin123', name: 'Admin' }
];

export const validateSession = (): boolean => {
  const sessionData = localStorage.getItem('sessionData');
  if (!sessionData) return false;

  try {
    const session = JSON.parse(sessionData);
    const now = Date.now();
    const timeSinceLastActivity = now - session.lastActivity;

    if (timeSinceLastActivity > SESSION_TIMEOUT) {
      clearSession();
      return false;
    }

    // Update last activity time
    session.lastActivity = now;
    localStorage.setItem('sessionData', JSON.stringify(session));
    return true;
  } catch {
    return false;
  }
};

export const clearSession = () => {
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userName');
  localStorage.removeItem('sessionData');
  localStorage.removeItem('username');
  localStorage.removeItem('userId');
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const widget = document.getElementById('vapi-widget-floating-button');
    if (widget) {
      (widget as HTMLElement).style.display = 'none';
    }

    return () => {
      const widget = document.getElementById('vapi-widget-floating-button');
      if (widget) {
        (widget as HTMLElement).style.display = '';
      }
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    // Check if user exists and password matches
    const user = VALID_USERS.find(u => u.email === email && u.password === password);

    if (user) {
      const sessionData = {
        email: user.email,
        name: user.name,
        loginTime: Date.now(),
        lastActivity: Date.now(),
        sessionId: Math.random().toString(36).substring(7)
      };
      
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userEmail', user.email);
      localStorage.setItem('userName', user.name);
      localStorage.setItem('sessionData', JSON.stringify(sessionData));
      
      navigate('/dashboard');
    } else {
      setError('Invalid email or password');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-md py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <Link to="/" className="inline-block mb-3">
              <img 
                src="https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/402bb47b1b6fd7c4f36ade1bab6a66a4.jpeg"
                alt="NatureIQ"
                className="h-16 w-auto mx-auto object-contain"
              />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Sparsh Intranet</h1>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center text-red-500"></i>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-mail-line w-5 h-5 flex items-center justify-center text-gray-400"></i>
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="your.email@natureiq.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line w-5 h-5 flex items-center justify-center text-gray-400"></i>
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                >
                  <i className={`ri-eye${showPassword ? '-off' : ''}-line w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Sign In
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
